import { useState, useRef, useEffect } from "react";
import { auth, db, storage } from "../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";

export default function ScreenCapture() {
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [videoURL, setVideoURL] = useState("");
  const [uploading, setUploading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const mediaRecorder = useRef(null);
  const recordedChunks = useRef([]);

  // ⏳ Auto-stop timer reference
  const timerRef = useRef(null);
  const AUTO_STOP_TIME = 60000; // 1 Minute

  // ✅ Fetch Company ID from Firestore
  useEffect(() => {
    const fetchCompanyId = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not authenticated.");
        }

        const companiesRef = collection(db, "companies");
        const companiesSnapshot = await getDocs(companiesRef);
        let foundCompanyId = null;

        for (const companyDoc of companiesSnapshot.docs) {
          const employeesRef = collection(
            db,
            `companies/${companyDoc.id}/employees`
          );
          const employeeDoc = await getDoc(doc(employeesRef, user.uid));

          if (employeeDoc.exists()) {
            foundCompanyId = companyDoc.id;
            break; // ✅ Stop searching once we find the company
          }
        }

        if (!foundCompanyId) {
          throw new Error("Company ID not found for this employee.");
        }

        setCompanyId(foundCompanyId);
      } catch (err) {
        console.error("Error fetching company ID:", err.message);
      }
    };

    // ✅ Ensure We Wait for User Authentication
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchCompanyId();
      }
    });

    return () => unsubscribe();
  }, []);

  // 🟢 Start Screen Capture with Auto Stop
  const startCapture = async () => {
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: "always" },
        audio: true,
      });

      setStream(displayStream);
      recordedChunks.current = [];

      const recorder = new MediaRecorder(displayStream, {
        mimeType: "video/webm",
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(recordedChunks.current, { type: "video/webm" });
        const videoUrl = URL.createObjectURL(blob);
        setVideoURL(videoUrl);

        // Upload video to Firebase Storage & Save in Firestore
        await uploadVideoToFirebase(blob);
      };

      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);

      // ⏳ Set auto-stop timer
      timerRef.current = setTimeout(() => {
        stopCapture();
      }, AUTO_STOP_TIME);
    } catch (err) {
      console.error("Error capturing screen:", err);
    }
  };

  // 🛑 Stop Screen Capture (Triggered Manually or via Auto-Stop)
  const stopCapture = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
    }

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }

    setRecording(false);

    // ⏳ Clear auto-stop timer if manually stopped
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // 📤 Upload video to Firebase Storage & Save in Company Subcollection
  const uploadVideoToFirebase = async (blob) => {
    if (!auth.currentUser) {
      alert("You must be logged in to save recordings.");
      return;
    }

    if (!companyId) {
      console.error("Company ID is not found. Cannot save recording.");
      return;
    }

    try {
      setUploading(true);
      const userId = auth.currentUser.uid;

      // 🔹 Step 1: Upload video to Firebase Storage
      const fileName = `screen_recordings/${companyId}/${userId}_${Date.now()}.webm`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, blob);
      const downloadUrl = await getDownloadURL(storageRef);

      // 🔹 Step 2: Save the video URL in Firestore under the company’s subcollection
      await addDoc(collection(db, `companies/${companyId}/screen_recordings`), {
        userId,
        videoUrl: downloadUrl,
        timestamp: serverTimestamp(),
      });

      setUploading(false);
      alert("Video uploaded successfully!");
    } catch (err) {
      console.error("Error uploading video:", err);
      setUploading(false);
    }
  };

  return (
    <div style={{ textAlign: "center", marginTop: "20px" }}>
      <h2>Screen Capture</h2>

      {stream && (
        <video
          ref={(video) => {
            if (video && stream && !recording) {
              video.srcObject = stream;
            }
          }}
          autoPlay
          playsInline
          style={{
            width: "80%",
            border: "1px solid black",
            marginBottom: "10px",
            display: recording ? "none" : "block", // ✅ Hide video when recording
          }}
        />
      )}

      <div>
        {!recording ? (
          <button onClick={startCapture} style={buttonStyle}>
            🎥 Start Capture
          </button>
        ) : (
          <button onClick={stopCapture} style={stopButtonStyle}>
            ⏹ Stop Capture
          </button>
        )}
      </div>

      {uploading && <p>Uploading to Firebase...</p>}

      {videoURL && (
        <div>
          <h3>Recorded Video</h3>
          <video src={videoURL} controls style={{ width: "80%" }} />
        </div>
      )}
    </div>
  );
}

// Button Styles
const buttonStyle = {
  padding: "10px 20px",
  backgroundColor: "green",
  color: "white",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  margin: "10px",
};

const stopButtonStyle = {
  ...buttonStyle,
  backgroundColor: "red",
};
